import { useTranslation } from 'react-i18next';

import { isSupportWidgetEnabled } from 'config/env';

import { Input } from 'components/Input';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { TextArea } from 'components/TextArea';

import { SupportTicketModal } from './SupportTicketModal';
import { useSupportTicketWidget } from './SupportTicketWidget.hooks';
import * as S from './SupportTicketWidget.styles';

// Matches colmar-be-nestjs's CreateSupportTicketDto (@MaxLength(1000) on
// description) — kept in sync by hand, same convention already accepted for
// the screenshot allow-list/size constants mirrored between FE and BE.
const DESCRIPTION_MAX_LENGTH = 1000;

export function SupportTicketWidget(): React.ReactElement | null {
  const { t } = useTranslation();

  const {
    isOpen,
    open,
    close,
    register,
    errors,
    onSubmit,
    isSubmitting,
    priority,
    onPriorityChange,
    priorityOptions,
    autoGrowDescription,
    descriptionLength,
    screenshotFiles,
    screenshotPreviewUrls,
    screenshotInputRef,
    handleScreenshotSelect,
    removeScreenshot,
    canAddMoreScreenshots,
  } = useSupportTicketWidget();

  if (!isSupportWidgetEnabled()) return null;

  return (
    <>
      <S.FloatingButton
        type="button"
        motive="m0"
        iconLeft="BsBug"
        aria-label={t('supportTicket.openWidget')}
        testID="support-ticket-widget-button"
        onClick={open}
      />

      {isOpen ? (
        <SupportTicketModal onClose={close}>
          <S.Form onSubmit={onSubmit}>
            <S.Title
              tag="h3"
              color="black"
              fontWeight="bold"
              fontFamily="LFT Zeno"
              textTransform="uppercase"
              scale="lg"
              isWrapped
            >
              {t('supportTicket.title')}
            </S.Title>

            <S.InputField>
              <Input
                name="subject"
                label={t('supportTicket.subject')}
                placeholder={t('supportTicket.subjectPlaceholder')}
                register={register}
                required
                error={errors.subject?.message}
                options={{ required: t('supportTicket.subjectRequired') }}
              />
            </S.InputField>

            <S.InputField>
              <TextArea
                name="description"
                label={t('supportTicket.description')}
                placeholder={t('supportTicket.descriptionPlaceholder')}
                register={register}
                required
                error={errors.description?.message}
                maxLength={DESCRIPTION_MAX_LENGTH}
                options={{
                  required: t('supportTicket.descriptionRequired'),
                  maxLength: {
                    value: DESCRIPTION_MAX_LENGTH,
                    message: t('supportTicket.descriptionMaxLengthError', {
                      count: DESCRIPTION_MAX_LENGTH,
                    }),
                  },
                }}
                onChange={autoGrowDescription}
              />

              <S.CharacterCount
                scale="s"
                color={descriptionLength >= DESCRIPTION_MAX_LENGTH ? 'red' : 'grayLabel'}
                textAlign="right"
              >
                {`${descriptionLength} / ${DESCRIPTION_MAX_LENGTH}`}
              </S.CharacterCount>
            </S.InputField>

            <S.Field>
              <Select
                name="priority"
                label={t('supportTicket.priority')}
                options={priorityOptions}
                value={priority}
                onChange={onPriorityChange}
                motive="supportTicket"
                isSearchable={false}
                menuPlacement="bottom"
              />
            </S.Field>

            <S.Field>
              <Label htmlFor="support-ticket-screenshot">{t('supportTicket.screenshot')}</Label>

              {canAddMoreScreenshots ? (
                <S.ScreenshotButton
                  type="button"
                  motive="cancelTertiary"
                  iconLeft="FiUpload"
                  icoarea="1.6"
                  scale="s"
                >
                  {t('supportTicket.attachScreenshot')}

                  <S.ScreenshotFileInput
                    id="support-ticket-screenshot"
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleScreenshotSelect}
                    ref={screenshotInputRef}
                    data-testid="support-ticket-screenshot-input"
                  />
                </S.ScreenshotButton>
              ) : null}

              {screenshotFiles.length > 0 ? (
                <S.ScreenshotList>
                  {screenshotFiles.map((file, index) => (
                    <S.ScreenshotPreview key={`${file.name}-${file.lastModified}-${file.size}`}>
                      {screenshotPreviewUrls[index] ? (
                        <S.ScreenshotThumbnail
                          src={screenshotPreviewUrls[index]}
                          alt={file.name}
                        />
                      ) : null}

                      <S.ScreenshotRemoveButton
                        type="button"
                        motive="m0"
                        iconLeft="FiX"
                        onClick={() => removeScreenshot(index)}
                        aria-label={t('supportTicket.removeScreenshot')}
                        testID={`support-ticket-remove-screenshot-button-${index}`}
                      />
                    </S.ScreenshotPreview>
                  ))}
                </S.ScreenshotList>
              ) : null}

              <S.ScreenshotHint scale="base" color="grayLabel" isWrapped>
                {t('supportTicket.screenshotHint')}
              </S.ScreenshotHint>
            </S.Field>

            <S.Footer>
              <S.SubmitButton
                type="submit"
                motive="submitPrimary"
                scale="base"
                testID="support-ticket-submit-button"
                isLoading={isSubmitting}
              >
                {t('supportTicket.submitTicket')}
              </S.SubmitButton>
            </S.Footer>
          </S.Form>
        </SupportTicketModal>
      ) : null}
    </>
  );
}
